import superjson from "superjson";
import { Either, Schema } from "effect";
import { decodeUnknownSync } from "../../../packages/common/src/schema.ts";
import {
  KitchenNotes,
  orderInformation,
  orderKitchenNotesSchema,
} from "../../../packages/common/src/schemas/kitchen-notes.ts";
import {
  findKitchenLine,
  kitchenLineKey,
  kitchenLineSchema,
  lineKitchenNotesSchema,
  type KitchenLine,
} from "../../../packages/common/src/schemas/kitchen-lines.ts";

/** Persisted states shared by live ordering and the staff status filters. */
export const orderStateSchema = Schema.Literal(
  "order_open",
  "order_paid_and_in_progress",
  "order_ready",
  "order_completed",
  "order_canceled",
  "order_refunded",
);

/** An operational order's lifecycle state. */
export type OrderState = typeof orderStateSchema.Type;

/** Staff-facing lifecycle labels. */
export const statusLabels: Record<OrderState, string> = {
  order_open: "Open",
  order_paid_and_in_progress: "In progress",
  order_ready: "Ready",
  order_completed: "Completed",
  order_canceled: "Cancelled",
  order_refunded: "Refunded",
};

/** States that must stay visible until staff finish the order. */
export const activeStates: readonly OrderState[] = [
  "order_open",
  "order_paid_and_in_progress",
  "order_ready",
];

/** Sentinel used by the kitchen filter for legacy or non-device orders. */
export const unassignedPaymentDevice = "__unassigned" as const;

const instant = Schema.String.pipe(
  Schema.filter(
    (value) =>
      (/^\d{4}-\d{2}-\d{2}T/.test(value) &&
        !Number.isNaN(Date.parse(value))) ||
      "Expected an ISO-8601 instant with an offset",
  ),
);

const row = Schema.Struct({
  id: Schema.String.pipe(Schema.minLength(1), Schema.brand("OrderRecordId")),
  order_id: Schema.String.pipe(
    Schema.minLength(1),
    Schema.brand("SquareOrderId"),
  ),
  // Square sync bookkeeping is part of the persisted record; Landpad ignores it.
  square_version: Schema.optional(Schema.Number),
  payment_device_id: Schema.optional(Schema.String),
  merchant_id: Schema.String.pipe(
    Schema.minLength(1),
    Schema.brand("MerchantId"),
  ),
  order_state: orderStateSchema,
  pickup_id: Schema.optional(Schema.NullOr(Schema.String)),
  scheduled_at: Schema.optional(
    Schema.NullOr(Schema.Union(instant, Schema.Literal(""))),
  ),
  created_at: instant,
  updated_at: Schema.optional(instant),
  revision: Schema.optional(Schema.UUID),
  order_details: Schema.optional(Schema.NullOr(Schema.String)),
  kitchen_notes: Schema.optional(orderKitchenNotesSchema),
  line_kitchen_notes: Schema.optional(lineKitchenNotesSchema),
});

const amount = Schema.transform(
  Schema.Union(
    Schema.BigIntFromSelf,
    Schema.Number.pipe(Schema.int(), Schema.finite()),
    Schema.String.pipe(Schema.pattern(/^-?\d+$/)),
  ),
  Schema.BigIntFromSelf,
  {
    decode: BigInt,
    encode: (value) => value,
  },
);

const money = Schema.Struct({
  amount,
  currency: Schema.optionalWith(Schema.String, { default: () => "AUD" }),
});

const line = Schema.Struct({
  ...kitchenLineSchema.fields,
  totalMoney: Schema.optional(money),
});

const details = Schema.Struct({
  locationId: Schema.optional(Schema.NullOr(Schema.NonEmptyString)),
  // Self-service kiosk orders carry the customer's name without a fulfillment.
  ticketName: Schema.optional(Schema.NullOr(Schema.String)),
  referenceId: Schema.optional(Schema.NullOr(Schema.String)),
  metadata: Schema.optional(
    Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.String })),
  ),
  lineItems: Schema.optional(Schema.Array(line)),
  returns: Schema.optional(
    Schema.Array(
      Schema.Struct({ returnLineItems: Schema.optional(Schema.Array(line)) }),
    ),
  ),
  totalMoney: Schema.optional(money),
  netAmounts: Schema.optional(
    Schema.Struct({ totalMoney: Schema.optional(money) }),
  ),
  fulfillments: Schema.optional(
    Schema.Array(
      Schema.Struct({
        pickupDetails: Schema.optional(
          Schema.Struct({
            pickupAt: Schema.optional(instant),
            recipient: Schema.optional(
              Schema.Struct({ displayName: Schema.optional(Schema.String) }),
            ),
          }),
        ),
      }),
    ),
  ),
});

/** Parsed order value. Provider serialization and database names stay at this boundary. */
export class Order {
  private constructor(
    private readonly record: typeof row.Type,
    private readonly contents: typeof details.Type | undefined,
  ) {}
  /** Parse a persistence record and preserve an explicit unreadable-details state. */
  static parse(record: typeof row.Type): Order {
    try {
      const decoded: unknown = record.order_details
        ? superjson.parse(record.order_details)
        : {};

      const parsed = Schema.decodeUnknownEither(details)(decoded);

      return new Order(
        record,
        Either.isRight(parsed) ? parsed.right : undefined,
      );
    } catch {
      return new Order(record, undefined);
    }
  }
  /** Persistence record used when an Effect schema encodes this value. */
  toRecord(): typeof row.Type {
    return this.record;
  }
  /** Local record ID used for mutations. */
  get id() {
    return this.record.id;
  }
  /** Square ID used for customer tracking. */
  get squareId() {
    return this.record.order_id;
  }
  /** Owning merchant, used in the authenticated mutation path. */
  get merchantId() {
    return this.record.merchant_id;
  }
  /** Square-issued device that accepted the payment, when available. */
  get paymentDeviceId() {
    return this.record.payment_device_id;
  }
  /** Square business location shared by cash and card orders. */
  get locationId() {
    return this.contents?.locationId ?? undefined;
  }
  /** Current lifecycle state. */
  get state() {
    return this.record.order_state;
  }
  /** Human-readable pickup name or ticket identifier. */
  get pickupName() {
    return (
      this.record.pickup_id ||
      this.contents?.fulfillments?.[0]?.pickupDetails?.recipient?.displayName ||
      this.contents?.ticketName ||
      ""
    );
  }
  /** Distinct identity even before staff assigns a pickup name. */
  get label() {
    return this.pickupName || this.squareId.slice(-8);
  }
  /** An explicit staff schedule overrides Square's pickup schedule. */
  get scheduledAt() {
    return this.record.scheduled_at === ""
      ? undefined
      : (this.record.scheduled_at ??
          this.contents?.fulfillments?.[0]?.pickupDetails?.pickupAt);
  }
  /** Original creation instant. */
  get createdAt() {
    return this.record.created_at;
  }
  /** Last known update, used to ignore stale realtime messages. */
  get updatedAt() {
    return this.record.updated_at ?? this.record.created_at;
  }
  /** Opaque database revision for a conditional update; legacy rows may not have one yet. */
  get revision() {
    return this.record.revision;
  }
  /** Typed order-wide information, including preserved earlier unassigned tags. */
  get information() {
    return orderInformation(this.record.kitchen_notes);
  }
  /** Whole-order quick tags never carry quantities or attach to individual items. */
  get quickTags() {
    return this.record.kitchen_notes?.quick_tags ?? [];
  }
  /** Preserve earlier counted annotations when editing only whole-order tags or text. */
  get orderNotes() {
    return this.record.kitchen_notes ?? { text: "", tags: [] };
  }
  /** Per-line staff instructions, never guessed among ambiguous lines. */
  notesFor(line: KitchenLine) {
    const key = kitchenLineKey(line);

    if (!findKitchenLine(this.lines, key)) return KitchenNotes.empty();

    const note = this.record.line_kitchen_notes?.find(
      (note) => note.line_key === key,
    );

    return note
      ? decodeUnknownSync(KitchenNotes.schema, {
          tags: note.tags,
          text: note.text,
        })
      : KitchenNotes.empty();
  }
  /** Removed/ambiguous item notes remain visible for manual review, never silently move to another row. */
  get unmatchedInstructions() {
    return (this.record.line_kitchen_notes ?? []).filter(
      (note) => !findKitchenLine(this.lines, note.line_key),
    );
  }
  /** Item details could not be decoded and should never look like an empty order. */
  get unreadable() {
    return this.contents === undefined;
  }
  /** Return-only orders display their returned items. */
  get lines() {
    return this.contents?.lineItems?.length
      ? this.contents.lineItems
      : (this.contents?.returns?.flatMap(
          (entry) => entry.returnLineItems ?? [],
        ) ?? []);
  }
  /** Exact minor-unit total, including return-only orders. */
  get total() {
    return this.contents?.totalMoney ?? this.contents?.netAmounts?.totalMoney;
  }
  /** Paid/open/ready orders require staff attention. */
  get active() {
    return activeStates.includes(this.state);
  }
  /** A kiosk customer chose "pay at the counter" and has not paid yet. */
  get awaitsCounterPayment() {
    return (
      this.state === "order_open" &&
      this.contents?.referenceId?.startsWith("kiosk:") === true &&
      this.contents.metadata?.kiosk_payment === "counter"
    );
  }
  /** Scheduled orders enter the kitchen 15 minutes before their preparation time. */
  isScheduled(now: number) {
    return (
      this.active &&
      Boolean(
        this.scheduledAt && Date.parse(this.scheduledAt) - now > 15 * 60_000,
      )
    );
  }
}

/** Parse HTTP/event rows directly into Order values. */
const OrderFromSelf = Schema.declare(
  (input): input is Order => input instanceof Order,
);
export const orderSchema = Schema.transform(row, OrderFromSelf, {
  decode: Order.parse,
  encode: (order) => order.toRecord(),
});

/** Keep delivery metadata on the mutation response, not on persisted orders. */
const orderMutationRecord = Schema.Struct({
  ...row.fields,
  live_update_status: Schema.optional(Schema.Literal("delayed")),
});
const orderMutationValue = Schema.Struct({
  order: OrderFromSelf,
  liveUpdatesDelayed: Schema.Boolean,
});
export const orderMutationSchema = Schema.NullOr(
  Schema.transform(orderMutationRecord, orderMutationValue, {
    decode: (record) => ({
      order: Order.parse(record),
      liveUpdatesDelayed: record.live_update_status === "delayed",
    }),
    encode: ({ order, liveUpdatesDelayed }) => ({
      ...order.toRecord(),
      ...(liveUpdatesDelayed ? { live_update_status: "delayed" as const } : {}),
    }),
  }),
);

/** Cursor-based history response. */
export const ordersPageSchema = Schema.Struct({
  _tag: Schema.Literal("OkOrders"),
  orders: Schema.Array(orderSchema),
  nextCursor: Schema.optional(Schema.NullOr(Schema.String)),
});

/** Single-order lookup response, also used for customer tracking. */
export const singleOrderSchema = Schema.Struct({
  data: Schema.Array(orderSchema),
});

/** Merge by local ID without replacing newer events with older snapshots. */
export function mergeOrders(
  current: readonly Order[],
  incoming: readonly Order[],
): Order[] {
  const result = new Map(current.map((order) => [order.id, order]));

  for (const order of incoming) {
    const previous = result.get(order.id);

    if (
      !previous ||
      Date.parse(order.updatedAt) >= Date.parse(previous.updatedAt)
    )
      result.set(order.id, order);
  }

  return [...result.values()];
}

/** Match one order against the locally applied payment-device filter. */
export function matchesPaymentDevice(
  order: Order,
  deviceIds: readonly string[],
): boolean {
  if (deviceIds.length === 0) return true;
  if (order.paymentDeviceId === undefined)
    return deviceIds.includes(unassignedPaymentDevice);
  return deviceIds.includes(order.paymentDeviceId);
}

/** Format exact minor units without coercing a bigint amount to floating point. */
export function formatMoney(
  value: { readonly amount: bigint; readonly currency: string } | undefined,
): string {
  if (!value) return "—";
  const magnitude = value.amount < 0n ? -value.amount : value.amount;
  const prefix = value.currency === "AUD" ? "$" : `${value.currency} `;

  return `${value.amount < 0n ? "−" : ""}${prefix}${(magnitude / 100n).toLocaleString("en-AU")}.${String(magnitude % 100n).padStart(2, "0")}`;
}

/** Sydney pickup time, including the calendar date for future bookings. */
export function formatTime(
  value: string,
  withDate = false,
  locale: string = "en-AU",
) {
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Australia/Sydney",
    hour: "numeric",
    minute: "2-digit",
    ...(withDate
      ? ({ weekday: "short", day: "numeric", month: "short" } as const)
      : {}),
  }).format(new Date(value));
}
